import { useEffect, useMemo, useRef, useState } from 'react';
import HomeHubShell from './HomeHubShell';
import JorvexStackRow from '../components/JorvexStackRow';

const C = {
  void: '#020508',
  deep: '#060b14',
  surface: '#0a1120',
  b0: '#0f1a2e',
  b1: '#1e293b',
  b2: '#334155',
  t1: '#e2e8f0',
  t2: '#b6c3d8',
  t3: '#8396b3',
  t4: '#627795',
  cyan: '#22d3ee',
  violet: '#a78bfa',
  green: '#4ade80',
  gold: '#fbbf24',
  red: '#f87171',
  orange: '#fb923c',
};

const DEMO_VIDEO_SRC = '/jorvex-promo.mp4';

const KEY_FEATURES = [
  {
    title: '100% Local + Offline',
    desc: 'Runs in-browser with local execution. Your data is not sent to servers by default.',
    icon: 'LOCAL',
    color: C.cyan,
  },
  {
    title: 'Privacy-First Workspace',
    desc: 'Datasets, prompts, and experiments stay private unless you explicitly export.',
    icon: 'SAFE',
    color: C.green,
  },
  {
    title: 'Dataset Intelligence',
    desc: 'Upload any dataset — tabular, text, images, audio, video. Run deep analysis & auto EDA.',
    icon: 'ANALYSIS',
    color: C.violet,
  },
  {
    title: 'Research Paper Analysis',
desc: 'Upload research papers. Extract insights, summaries, key ideas automatically.',    icon: 'PRO',
    color: C.red,
  },
  
  {
    title: 'Create AI Workers',
    desc: 'Create AIs to work on your datasets, experiments, and ongoing research.',
    icon: 'AI',
    color: C.orange,
  },
  {
    title: 'Save Results + Checkpoints',
    desc: 'Persist outputs, checkpoints, and run states so experiments remain reproducible.',
    icon: 'SAVE',
    color: C.gold,
  },
  
];

const DESIGNED_FOR = [
  {
    title: 'AI/ML Learners',
    desc: 'Learn by running interactive labs, simulations, and guided neural network experiments.',
    icon: 'LEARN',
    color: C.cyan,
  },
  {
    title: 'AI/ML Experimenters & Builders',
    desc: 'Upload data, run pipelines, compare optimizers, and version reproducible experiment results.',
    icon: 'BUILD',
    color: C.green,
  },
  {
    title: 'AI/ML Researchers',
    desc: 'Coordinate multi-source research with AI agents, schedule runs, and publish commit trails.',
    icon: 'RESEARCH',
    color: C.violet,
  },
];

const DIFFERENTIATION = [
  {
    tool: 'Jupyter / Google Colab',
    points: ['Great for notebooks', 'Manual tracking', 'No persistent AI collaborator'],
    degen: 'Versioned experiments by default with AI agents that remember your research history.',
    color: C.cyan,
  },
  {
    tool: 'Weights & Biases / MLflow / Neptune',
    points: ['Powerful tracking', 'Setup complexity', 'No built-in learning simulation layer'],
    degen: 'Zero-setup experimentation with built-in AI agents and a learning-to-research workflow.',
    color: C.violet,
  },
  {
    tool: 'ChatGPT / Claude / Gemini',
    points: ['Helpful responses', 'No experiment awareness', 'No persistent project memory'],
    degen: 'AI agents connected to datasets, model runs, and experiment history inside one workspace.',
    color: C.green,
  },
  {
    tool: 'Kaggle',
    points: ['Great datasets and competitions', 'Notebook-first workflow', 'No longitudinal memory layer'],
    degen: 'Structured experiment engine with version tracking and a persistent AI research partner.',
    color: C.gold,
  },
 
];

const SOCIAL_PROOF = [
  { label: 'Researchers', value: '39', color: C.cyan },
  { label: 'Experiments Run', value: '107', color: C.green },
  { label: 'On-Chain Records', value: '15', color: C.gold },
  { label: 'AI Agents Active', value: '7', color: C.violet },
];

const PRICING_PREVIEW = [
  {
    tier: 'Learner',
    price: 'Free',
    note: 'Interactive labs and simulation-first learning.',
    icon: 'LRN',
    emoji: '🧠',
    bullets: ['Guided labs', 'Live simulations', 'Learning track'],
    color: C.cyan,
  },
  {
    tier: 'Researcher Quarterly',
    price: '$69',
    note: 'Flexible paid access for uninterrupted experiments and reports.',
    icon: 'BLD',
    emoji: '⚙️',
    bullets: ['Quarterly billing', 'Unlimited workflows', 'Versioned outputs'],
    color: C.green,
    highlight: true,
  },
  {
    tier: 'Researcher Yearly',
    price: '$199/yr',
    note: 'Best-value annual plan for builders and researchers running continuously.',
    icon: 'RSH',
    emoji: '🔬',
    bullets: ['Best annual pricing', 'Agent collaboration', 'Multi-source research'],
    color: C.violet,
  },
];

const AGENTS = [
  {
    name: 'NEXUS-7',
    color: C.cyan,
    icon: 'N',
    vitality: 78,
    spec: 'Quantum ML',
    finding: 'Anomaly in fidelity at t=2.4ms. Correlates with temperature drift in batch 3.',
  },
  {
    name: 'VERA',
    color: C.violet,
    icon: 'V',
    vitality: 91,
    spec: 'Statistics',
    finding: 'Distribution shift detected in cohort 3, p=0.003. Validation split needs correction.',
  },
  {
    name: 'ORACLE',
    color: C.gold,
    icon: 'O',
    vitality: 95,
    spec: 'Synthesis',
    finding: 'Current reward curve mirrors prior protein-folding runs. Same instability class.',
  },
];

const LAB_TABS = [
  { id: 'learner', label: 'Learner', color: C.cyan },
  { id: 'builder', label: 'Builder', color: C.green },
  { id: 'researcher', label: 'Researcher', color: C.violet },
];

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShow(true);
      },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'none' : 'translateY(14px)',
        transition: `opacity .55s ease ${delay}s, transform .55s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function ProgressBar({ value, color }) {
  return (
    <div style={{ height: 3, width: '100%', background: C.b1, borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${value}%`, background: color, transition: 'width 1.1s ease' }} />
    </div>
  );
}

function MiniMetric({ label, value, color }) {
  return (
    <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.surface, padding: '8px 10px' }}>
      <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.1em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: color || C.t1, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function LinePreview({ points, color }) {
  if (!points.length) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(0.00001, max - min);
  const toSvg = points
    .map((v, i) => {
      const x = (i / Math.max(1, points.length - 1)) * 100;
      const y = 90 - ((v - min) / range) * 80;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: 110, background: C.b0, border: `1px solid ${C.b1}`, borderRadius: 4 }}>
      <polyline fill="none" stroke={color} strokeWidth="2.5" points={toSvg} />
    </svg>
  );
}

function TopologyDiagram({ layers, activations }) {
  const width = 520;
  const height = 270;
  const maxDraw = 7;
  const layerGap = width / (layers.length + 1);
  const layerColors = layers.map((_, idx) => {
    if (idx === 0) return C.cyan;
    if (idx === layers.length - 1) return C.green;
    return C.violet;
  });

  const nodes = layers.map((count, li) => {
    const drawCount = Math.min(count, maxDraw);
    const gap = height / (drawCount + 1);
    return Array.from({ length: drawCount }, (_, ni) => ({
      x: layerGap * (li + 1),
      y: gap * (ni + 1),
      li,
      ni,
      total: count,
      overflow: ni === drawCount - 1 && count > drawCount,
    }));
  });

  const flatActs = activations ? activations.flat().filter((v) => typeof v === 'number') : [];
  const maxAbs = flatActs.length ? Math.max(0.001, ...flatActs.map((v) => Math.abs(v))) : 1;

  return (
    <svg viewBox={`0 0 ${width} ${height + 28}`} style={{ width: '100%', height: 300, display: 'block' }}>
      {nodes.slice(0, -1).map((layer, li) =>
        layer
          .slice(0, 6)
          .flatMap((from) =>
            nodes[li + 1].slice(0, 6).map((to) => (
              <line
                key={`edge-${li}-${from.ni}-${to.ni}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={C.b2}
                strokeWidth="0.8"
                opacity="0.45"
              />
            ))
          )
      )}

      {nodes.map((layer) =>
        layer.map((node) => {
          const color = layerColors[node.li];
          const activation = activations?.[node.li]?.[node.ni];
          const strength = activation === undefined ? 0 : Math.min(1, Math.abs(activation) / maxAbs);
          if (node.overflow) {
            return (
              <g key={`node-overflow-${node.li}-${node.ni}`}>
                <text x={node.x} y={node.y + 4} textAnchor="middle" fill={color} fontSize="18">
                  ...
                </text>
                <text x={node.x} y={node.y + 18} textAnchor="middle" fill={C.t3} fontSize="8">
                  +{node.total - layer.length + 1}
                </text>
              </g>
            );
          }
          return (
            <g key={`node-${node.li}-${node.ni}`}>
              <circle
                cx={node.x}
                cy={node.y}
                r={11}
                fill={activation === undefined ? `${color}16` : `${activation >= 0 ? color : C.red}${Math.round(35 + strength * 170)
                  .toString(16)
                  .padStart(2, '0')}`}
                stroke={activation === undefined ? color : activation >= 0 ? color : C.red}
                strokeWidth={activation === undefined ? '1.4' : '1.8'}
              />
              {activation !== undefined && (
                <text x={node.x} y={node.y + 3} textAnchor="middle" fontSize="7" fill={strength > 0.55 ? C.void : C.t1} fontWeight="700">
                  {activation.toFixed(1)}
                </text>
              )}
            </g>
          );
        })
      )}

      {layers.map((layerSize, idx) => (
        <g key={`label-${idx}`}>
          <text x={layerGap * (idx + 1)} y={height + 12} textAnchor="middle" fill={C.t3} fontSize="8">
            {idx === 0 ? 'INPUT' : idx === layers.length - 1 ? 'OUTPUT' : `H${idx}`}
          </text>
          <text x={layerGap * (idx + 1)} y={height + 24} textAnchor="middle" fill={layerColors[idx]} fontSize="9" fontWeight="700">
            {layerSize}
          </text>
        </g>
      ))}
    </svg>
  );
}

function stableNoise(seed) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453123;
  return (x - Math.floor(x)) * 2 - 1;
}

function applyActivation(type, value) {
  if (type === 'relu') return Math.max(0, value);
  if (type === 'gelu') {
    const c = 0.044715;
    return 0.5 * value * (1 + Math.tanh(0.7978845608 * (value + c * value * value * value)));
  }
  if (type === 'swish') return value / (1 + Math.exp(-value));
  if (type === 'tanh') return Math.tanh(value);
  return value;
}

function pseudoHex(seed, length = 64) {
  const HEX = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const n = stableNoise(seed * 17 + i * 29 + 0.13);
    const idx = Math.max(0, Math.min(15, Math.floor(((n + 1) / 2) * 15.999)));
    out += HEX[idx];
  }
  return out;
}

function pseudoTxHash(seed) {
  return `0x${pseudoHex(seed, 64)}`;
}

function InteractiveLabsSection() {
  const [activeTab, setActiveTab] = useState('learner');

  const [learnerArchitecture, setLearnerArchitecture] = useState([2, 8, 8, 1]);
  const [learnerActivation, setLearnerActivation] = useState('gelu');
  const [learnerInit, setLearnerInit] = useState('xavier');
  const [learnerLr, setLearnerLr] = useState(0.01);
  const [learnerRunId, setLearnerRunId] = useState(1);
  const [learnerForwardData, setLearnerForwardData] = useState(null);

  const [builderOptimizer, setBuilderOptimizer] = useState('adam');
  const [builderSplit, setBuilderSplit] = useState(80);
  const [builderArchitecture, setBuilderArchitecture] = useState('16');
  const [builderRunSeed, setBuilderRunSeed] = useState(1);
  const [builderEpochCursor, setBuilderEpochCursor] = useState(0);
  const [builderIsTraining, setBuilderIsTraining] = useState(false);
  const [builderVersions, setBuilderVersions] = useState([]);

  const [researchInit, setResearchInit] = useState('xavier');
  const [researchAlgo, setResearchAlgo] = useState('adamw');
  const [researchActivation, setResearchActivation] = useState('gelu');
  const [researchDepth, setResearchDepth] = useState(12);
  const [researchSeed, setResearchSeed] = useState(1);
  const [researchSchedule, setResearchSchedule] = useState('hourly');
  const [researchPriority, setResearchPriority] = useState(3);
  const [researchAgent, setResearchAgent] = useState('NEXUS-7');
  const [researchTask, setResearchTask] = useState('Draft hypothesis report');
  const [researchRunId, setResearchRunId] = useState(1);
  const [researchProgress, setResearchProgress] = useState(0);
  const [researchIsRunning, setResearchIsRunning] = useState(false);
  const [researchHasRun, setResearchHasRun] = useState(false);
  const [researchFinalizedRun, setResearchFinalizedRun] = useState(0);
  const [researchAssignments, setResearchAssignments] = useState([]);
  const [researchScheduleQueue, setResearchScheduleQueue] = useState([]);
  const [researchFindings, setResearchFindings] = useState([]);
  const [researchCommits, setResearchCommits] = useState([]);
  const [researchSources, setResearchSources] = useState([
    { id: 'kaggle_loans', name: 'Kaggle: Loan Risk 2025', type: 'Kaggle', connected: true, rows: '48.7K' },
    { id: 'hf_policy', name: 'HuggingFace: Policy QA', type: 'HF Dataset', connected: true, rows: '12.4K' },
    { id: 'postgres_risk', name: 'Postgres: risk_features', type: 'Postgres', connected: false, rows: '3.2M' },
    { id: 's3_docs', name: 'S3: underwriting-reports', type: 'S3', connected: true, rows: '2.1K docs' },
    { id: 'gsheets_ops', name: 'GSheets: analyst_flags', type: 'GSheets', connected: false, rows: '8 tabs' },
  ]);

  const learnerTotalParams = useMemo(
    () =>
      learnerArchitecture.reduce((sum, neurons, idx) => {
        if (idx === 0) return sum;
        return sum + neurons * learnerArchitecture[idx - 1] + neurons;
      }, 0),
    [learnerArchitecture]
  );

  const learnerForwardStats = useMemo(() => {
    if (!learnerForwardData) return null;
    const layers = learnerForwardData.activations;
    const nonInputLayers = layers.slice(1);
    const flatNonInput = nonInputLayers.flat();
    const absValues = flatNonInput.map((v) => Math.abs(v));
    const maxAbs = absValues.length ? Math.max(...absValues) : 0;
    const meanAbs = absValues.length ? absValues.reduce((s, v) => s + v, 0) / absValues.length : 0;
    const deadCount = flatNonInput.filter((v) => Math.abs(v) < 0.0001).length;
    const deadRatio = flatNonInput.length ? (deadCount / flatNonInput.length) * 100 : 0;

    const layerRows = nonInputLayers.map((layerVals, idx) => {
      const mean = layerVals.reduce((s, v) => s + v, 0) / Math.max(1, layerVals.length);
      const min = Math.min(...layerVals);
      const max = Math.max(...layerVals);
      const meanAbsLayer = layerVals.reduce((s, v) => s + Math.abs(v), 0) / Math.max(1, layerVals.length);
      return {
        layer: idx === nonInputLayers.length - 1 ? 'OUTPUT' : `HIDDEN ${idx + 1}`,
        mean,
        min,
        max,
        meanAbs: meanAbsLayer,
      };
    });

    const outputLayer = nonInputLayers[nonInputLayers.length - 1] || [];
    const outputConfidence = outputLayer.length
      ? outputLayer.reduce((s, v) => s + Math.abs(v), 0) / outputLayer.length
      : 0;

    return {
      meanAbs,
      maxAbs,
      deadCount,
      deadRatio,
      outputConfidence,
      layerRows,
      line: layerRows.map((row) => row.meanAbs),
    };
  }, [learnerForwardData]);

  const builderEpochs = useMemo(() => {
    if (builderArchitecture === '8') return 26;
    if (builderArchitecture === '32') return 54;
    return 40;
  }, [builderArchitecture]);

  const builderLossHistory = useMemo(() => {
    const depth = Number(builderArchitecture);
    const optStart = builderOptimizer === 'sgd' ? 1.62 : builderOptimizer === 'adam' ? 1.48 : 1.34;
    const decay = builderOptimizer === 'sgd' ? 0.068 : builderOptimizer === 'adam' ? 0.087 : 0.098;
    const depthPenalty = depth === 32 ? 0.09 : depth === 8 ? -0.04 : 0.02;
    const splitBenefit = (builderSplit - 70) * 0.0025;
    return Array.from({ length: builderEpochs }, (_, idx) => {
      const noise = stableNoise((idx + 1) * 31 + builderRunSeed * 19) * 0.015;
      const value = optStart * Math.exp(-(idx + 1) * decay) + 0.07 + depthPenalty - splitBenefit + noise;
      return Number(Math.max(0.04, value).toFixed(4));
    });
  }, [builderArchitecture, builderOptimizer, builderSplit, builderEpochs, builderRunSeed]);

  const builderProgress = builderEpochCursor > 0 ? Math.min(1, builderEpochCursor / builderEpochs) : 0;
  const builderVisibleLoss = builderLossHistory.slice(0, Math.max(2, builderEpochCursor || 2));
  const builderCurrentLoss = builderEpochCursor > 0 ? builderLossHistory[Math.max(0, builderEpochCursor - 1)] : null;

  const builderTargetMetrics = useMemo(() => {
    const depth = Number(builderArchitecture);
    const optBoost = builderOptimizer === 'adamw' ? 4.8 : builderOptimizer === 'adam' ? 3.4 : 1.6;
    const archBoost = depth === 16 ? 2.6 : depth === 32 ? 1.9 : 1.1;
    const splitBoost = Math.max(-1.2, Math.min(4.2, (builderSplit - 70) * 0.22));
    const jitter = (builderRunSeed % 7) * 0.28;
    const finalLoss = builderLossHistory[builderLossHistory.length - 1];
    const accuracy = Math.min(97.9, Math.max(78.8, 86.2 + optBoost + archBoost + splitBoost + jitter - finalLoss * 8));
    const precision = Math.max(72, Math.min(97, accuracy - 0.7 + (depth === 32 ? 0.4 : 0)));
    const recall = Math.max(70, Math.min(96, accuracy - 1.2 + (builderOptimizer === 'adamw' ? 0.6 : 0)));
    const f1 = (2 * precision * recall) / Math.max(1, precision + recall);
    const auc = Math.max(75, Math.min(99, accuracy + 1.8));
    const latency = Math.max(28, 86 - optBoost * 4 - (depth / 16) * 8 + (builderRunSeed % 5) * 1.4);
    const runScore = Math.max(60, Math.min(99, accuracy * 0.56 + f1 * 0.32 + (100 - latency) * 0.12));
    return { accuracy, precision, recall, f1, auc, latency, runScore };
  }, [builderArchitecture, builderOptimizer, builderSplit, builderRunSeed, builderLossHistory]);

  const builderMetrics = useMemo(() => {
    const p = Math.max(0.04, builderProgress);
    const start = {
      accuracy: 57.5,
      precision: 55.8,
      recall: 53.6,
      f1: 54.2,
      auc: 58.1,
      latency: 166,
      runScore: 34.5,
    };
    return {
      accuracy: start.accuracy + (builderTargetMetrics.accuracy - start.accuracy) * p,
      precision: start.precision + (builderTargetMetrics.precision - start.precision) * p,
      recall: start.recall + (builderTargetMetrics.recall - start.recall) * p,
      f1: start.f1 + (builderTargetMetrics.f1 - start.f1) * p,
      auc: start.auc + (builderTargetMetrics.auc - start.auc) * p,
      latency: start.latency + (builderTargetMetrics.latency - start.latency) * p,
      runScore: start.runScore + (builderTargetMetrics.runScore - start.runScore) * p,
    };
  }, [builderTargetMetrics, builderProgress]);

  const builderConfusion = useMemo(() => {
    const positives = 1260;
    const negatives = 1640;
    const recall = builderMetrics.recall / 100;
    const precision = builderMetrics.precision / 100;
    const tp = Math.round(positives * recall);
    const fn = Math.max(0, positives - tp);
    const fp = Math.round(tp * (1 / Math.max(0.01, precision) - 1));
    const tn = Math.max(0, negatives - fp);
    return { tp, fp, fn, tn };
  }, [builderMetrics]);

  const builderNetworkLayers = useMemo(() => {
    if (builderArchitecture === '8') return [26, 20, 14, 9, 5, 1];
    if (builderArchitecture === '32') return [26, 34, 30, 26, 22, 18, 14, 10, 6, 3, 1];
    return [26, 28, 22, 16, 10, 5, 1];
  }, [builderArchitecture]);

  const builderHasRun = builderEpochCursor > 0;

  const builderNetworkActivations = useMemo(() => {
    if (!builderHasRun) return null;
    return builderNetworkLayers.map((layerSize, layerIdx) =>
      Array.from({ length: layerSize }, (_, neuronIdx) => {
        if (layerIdx === 0) {
          return Number((stableNoise((neuronIdx + 1) * 17 + builderRunSeed * 9) * 1.1).toFixed(2));
        }
        const base = stableNoise((layerIdx + 1) * 97 + (neuronIdx + 1) * 23 + builderRunSeed * 11);
        const damping = Math.max(0.22, 1 - layerIdx * 0.07);
        const amp = 0.4 + builderProgress * (layerIdx === builderNetworkLayers.length - 1 ? 0.95 : 0.72);
        return Number((base * damping * amp).toFixed(2));
      })
    );
  }, [builderHasRun, builderNetworkLayers, builderRunSeed, builderProgress]);

  const builderTrace = useMemo(() => {
    if (!builderHasRun) return [];
    const start = Math.max(1, builderEpochCursor - 6);
    return Array.from({ length: builderEpochCursor - start + 1 }, (_, i) => {
      const epoch = start + i;
      const loss = builderLossHistory[epoch - 1];
      const gradNorm = Math.max(0.001, loss * (builderOptimizer === 'sgd' ? 0.82 : builderOptimizer === 'adam' ? 0.64 : 0.58));
      const lr = builderOptimizer === 'sgd' ? 0.01 : builderOptimizer === 'adam' ? 0.001 : 0.0007;
      return {
        epoch,
        loss,
        gradNorm,
        lr,
      };
    });
  }, [builderHasRun, builderEpochCursor, builderLossHistory, builderOptimizer]);

  const connectedSources = useMemo(() => researchSources.filter((s) => s.connected).length, [researchSources]);

  const researcherTarget = useMemo(() => {
    const initScore = researchInit === 'xavier' ? 84 : researchInit === 'he' ? 82 : 87;
    const algoScore = researchAlgo === 'adamw' ? 89 : researchAlgo === 'lion' ? 86 : 91;
    const actScore = researchActivation === 'gelu' ? 90 : researchActivation === 'swish' ? 88 : 83;
    const depthCurve = Math.max(-5, 7 - Math.abs(researchDepth - 18) * 0.55);
    const sourceBoost = connectedSources * 1.9;
    const scheduleBoost = researchSchedule === 'stream' ? 3.1 : researchSchedule === 'hourly' ? 2.2 : researchSchedule === 'daily' ? 1.4 : 0.8;
    const priorityBoost = researchPriority * 1.2;
    const seedNoise = (researchSeed % 9) * 0.45;
    const assignmentBoost = Math.min(6, researchAssignments.length * 1.1);

    const stability = Math.max(62, Math.min(99, (initScore + algoScore + actScore) / 3 + depthCurve + sourceBoost * 0.35 + seedNoise));
    const novelty = Math.max(58, Math.min(99, 70 + depthCurve * 0.4 + scheduleBoost + priorityBoost + assignmentBoost + seedNoise));
    const reproducibility = Math.max(60, Math.min(99, stability + sourceBoost * 0.6 + (researchAlgo === 'shampoo' ? -1.8 : 2.3)));
    const evidence = Math.max(56, Math.min(99, 65 + sourceBoost * 2.2 + priorityBoost + assignmentBoost + scheduleBoost));
    const agentSync = Math.max(55, Math.min(99, 66 + assignmentBoost * 1.6 + scheduleBoost + priorityBoost * 0.8));
    return { stability, novelty, reproducibility, evidence, agentSync };
  }, [
    researchInit,
    researchAlgo,
    researchActivation,
    researchDepth,
    connectedSources,
    researchSchedule,
    researchPriority,
    researchSeed,
    researchAssignments,
  ]);

  const researcherMetrics = useMemo(() => {
    if (!researchHasRun) {
      return {
        stability: 0,
        novelty: 0,
        reproducibility: 0,
        evidence: 0,
        agentSync: 0,
      };
    }
    const p = Math.max(0.06, researchProgress);
    const start = {
      stability: 58,
      novelty: 55,
      reproducibility: 57,
      evidence: 52,
      agentSync: 54,
    };
    return {
      stability: start.stability + (researcherTarget.stability - start.stability) * p,
      novelty: start.novelty + (researcherTarget.novelty - start.novelty) * p,
      reproducibility: start.reproducibility + (researcherTarget.reproducibility - start.reproducibility) * p,
      evidence: start.evidence + (researcherTarget.evidence - start.evidence) * p,
      agentSync: start.agentSync + (researcherTarget.agentSync - start.agentSync) * p,
    };
  }, [researcherTarget, researchHasRun, researchProgress]);

  const researchTopologyLayers = useMemo(
    () => [Math.max(10, connectedSources * 4), Math.max(14, researchDepth + 8), researchDepth, Math.max(6, Math.round(researchDepth * 0.6)), 2],
    [connectedSources, researchDepth]
  );

  const researchTopologyActivations = useMemo(() => {
    if (!researchHasRun) return null;
    return researchTopologyLayers.map((layerSize, layerIdx) =>
      Array.from({ length: layerSize }, (_, neuronIdx) => {
        const base = stableNoise((layerIdx + 1) * 73 + (neuronIdx + 1) * 31 + researchRunId * 17);
        const amp = 0.32 + researchProgress * (layerIdx === researchTopologyLayers.length - 1 ? 0.9 : 0.7);
        return Number((base * amp).toFixed(2));
      })
    );
  }, [researchHasRun, researchTopologyLayers, researchRunId, researchProgress]);

  const researchObjectiveLine = useMemo(() => {
    if (!researchHasRun) return [];
    const points = Math.max(4, Math.round(researchProgress * 36));
    return Array.from({ length: points }, (_, i) => {
      const decay = 0.11 + (researchAlgo === 'adamw' ? 0.03 : researchAlgo === 'lion' ? 0.025 : 0.035);
      const depthAdj = Math.max(0, (researchDepth - 14) * 0.004);
      const v = 1.06 * Math.exp(-(i + 1) * decay) + 0.09 + depthAdj + stableNoise((i + 1) * 41 + researchRunId * 13) * 0.01;
      return Number(Math.max(0.03, v).toFixed(4));
    });
  }, [researchHasRun, researchProgress, researchAlgo, researchDepth, researchRunId]);

  const researchAgentBoard = useMemo(() => {
    return AGENTS.map((agent, idx) => {
      const assigned = researchAssignments.find((a) => a.agent === agent.name);
      const utilBase = 54 + idx * 9;
      const util = Math.min(
        99,
        utilBase +
          (assigned ? 16 : 0) +
          connectedSources * 2.4 +
          researchPriority * 1.8 +
          (researchHasRun ? researchProgress * 8 : 0)
      );
      return {
        ...agent,
        utilization: util,
        assignedTask: assigned?.task || 'Awaiting assignment',
        status: assigned ? (researchIsRunning ? 'ACTIVE' : 'READY') : 'IDLE',
      };
    });
  }, [researchAssignments, connectedSources, researchPriority, researchHasRun, researchProgress, researchIsRunning]);

  const addHiddenLayer = () =>
    setLearnerArchitecture((prev) => {
      if (prev.length >= 8) return prev;
      const newSize = Math.max(4, prev[prev.length - 2]);
      return [...prev.slice(0, -1), newSize, prev[prev.length - 1]];
    });

  const removeHiddenLayer = () =>
    setLearnerArchitecture((prev) => {
      if (prev.length <= 3) return prev;
      return [...prev.slice(0, -2), prev[prev.length - 1]];
    });

  const updateLayerSize = (layerIdx, value) =>
    setLearnerArchitecture((prev) => {
      const next = [...prev];
      next[layerIdx] = Math.max(1, Math.min(96, Number(value) || 1));
      return next;
    });

  const runLearnerForwardPass = () => {
    const initScale = learnerInit === 'he' ? 1.1 : learnerInit === 'xavier' ? 0.9 : learnerInit === 'lecun' ? 0.78 : learnerInit === 'zeros' ? 0.02 : 1.25;
    const input = Array.from({ length: learnerArchitecture[0] }, (_, idx) => Number((stableNoise((idx + 1) * 17 + learnerRunId * 11) * 1.2).toFixed(4)));
    const activations = [input];
    const tableRows = [];

    for (let layerIdx = 1; layerIdx < learnerArchitecture.length; layerIdx += 1) {
      const prev = activations[layerIdx - 1];
      const layerSize = learnerArchitecture[layerIdx];
      const preVals = Array.from({ length: layerSize }, (_, neuronIdx) => {
        const weighted = prev.reduce((sum, val, prevIdx) => {
          const w = stableNoise((layerIdx + 1) * 101 + (neuronIdx + 1) * 37 + (prevIdx + 1) * 19 + learnerRunId * 13) * initScale;
          return sum + val * w;
        }, 0);
        const bias = stableNoise((layerIdx + 1) * 211 + (neuronIdx + 1) * 17 + learnerRunId * 7) * 0.18;
        return weighted / Math.max(1, prev.length) + bias;
      });
      const actVals = preVals.map((value) => applyActivation(learnerActivation, value));
      activations.push(actVals);

      preVals.slice(0, 14).forEach((z, neuronIdx) => {
        tableRows.push({
          key: `${layerIdx}-${neuronIdx}`,
          layer: layerIdx === learnerArchitecture.length - 1 ? 'OUTPUT' : `HIDDEN ${layerIdx}`,
          neuron: neuronIdx + 1,
          z: Number(z.toFixed(4)),
          a: Number(actVals[neuronIdx].toFixed(4)),
        });
      });
    }

    setLearnerForwardData({
      input,
      activations,
      tableRows,
    });
    setLearnerRunId((n) => n + 1);
  };

  useEffect(() => {
    setLearnerForwardData(null);
  }, [learnerArchitecture, learnerActivation, learnerInit, learnerLr]);

  useEffect(() => {
    setBuilderEpochCursor(0);
    setBuilderIsTraining(false);
  }, [builderOptimizer, builderArchitecture, builderSplit]);

  useEffect(() => {
    if (!builderIsTraining) return undefined;
    if (builderEpochCursor >= builderEpochs) {
      setBuilderIsTraining(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setBuilderEpochCursor((prev) => Math.min(builderEpochs, prev + 1));
    }, 80);
    return () => clearTimeout(timer);
  }, [builderIsTraining, builderEpochCursor, builderEpochs]);

  const runBuilderTraining = () => {
    setBuilderRunSeed((n) => n + 1);
    setBuilderEpochCursor(1);
    setBuilderIsTraining(true);
  };

  const saveBuilderVersion = () => {
    if (builderEpochCursor === 0) return;
    const stamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const version = {
      id: `v${builderVersions.length + 1}.${builderRunSeed}`,
      stamp,
      optimizer: builderOptimizer.toUpperCase(),
      architecture: `${builderArchitecture}L`,
      split: builderSplit,
      loss: (builderCurrentLoss ?? builderLossHistory[builderLossHistory.length - 1]).toFixed(3),
      acc: builderMetrics.accuracy.toFixed(1),
      f1: builderMetrics.f1.toFixed(1),
    };
    setBuilderVersions((prev) => [version, ...prev].slice(0, 8));
  };

  useEffect(() => {
    if (!researchIsRunning) return undefined;
    if (researchProgress >= 1) {
      setResearchIsRunning(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setResearchProgress((prev) => Math.min(1, prev + 0.08));
    }, 220);
    return () => clearTimeout(timer);
  }, [researchIsRunning, researchProgress]);

  useEffect(() => {
    if (researchProgress < 1 || researchRunId === researchFinalizedRun) return;
    setResearchFinalizedRun(researchRunId);
    const conclusion = `Run #${researchRunId} complete: stability ${researcherTarget.stability.toFixed(1)}%, evidence ${researcherTarget.evidence.toFixed(1)}%.`;
    setResearchFindings((prev) => [
      {
        id: `finding-final-${researchRunId}`,
        ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: 'RESULT',
        text: conclusion,
      },
      ...prev,
    ].slice(0, 14));
  }, [researchProgress, researchRunId, researchFinalizedRun, researcherTarget]);

  const toggleResearchSource = (sourceId) => {
    setResearchSources((prev) => prev.map((source) => (source.id === sourceId ? { ...source, connected: !source.connected } : source)));
  };

  const assignResearchWork = () => {
    const assignment = {
      id: `assign-${Date.now()}`,
      agent: researchAgent,
      task: researchTask,
      priority: researchPriority,
      schedule: researchSchedule,
      ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setResearchAssignments((prev) => [assignment, ...prev.filter((a) => a.agent !== researchAgent)].slice(0, 8));
    setResearchFindings((prev) => [
      {
        id: `finding-assign-${Date.now()}`,
        ts: assignment.ts,
        type: 'ASSIGN',
        text: `${researchAgent} assigned: ${researchTask}. Priority P${researchPriority}.`,
      },
      ...prev,
    ].slice(0, 14));
  };

  const scheduleResearchExperiment = () => {
    const scheduleItem = {
      id: `sched-${Date.now()}`,
      cadence: researchSchedule,
      config: `${researchAlgo}/${researchActivation}/d${researchDepth}`,
      ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setResearchScheduleQueue((prev) => [scheduleItem, ...prev].slice(0, 8));
    setResearchFindings((prev) => [
      {
        id: `finding-schedule-${Date.now()}`,
        ts: scheduleItem.ts,
        type: 'SCHED',
        text: `Scheduled ${scheduleItem.cadence} experiment (${scheduleItem.config}) with ${connectedSources} active sources.`,
      },
      ...prev,
    ].slice(0, 14));
  };

  const runResearchScenario = () => {
    const nextRun = researchRunId + 1;
    setResearchRunId(nextRun);
    setResearchSeed((n) => n + 1);
    setResearchHasRun(true);
    setResearchIsRunning(true);
    setResearchProgress(0.02);
    setResearchFindings((prev) => [
      {
        id: `finding-run-${nextRun}`,
        ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: 'RUN',
        text: `Run #${nextRun} started with ${connectedSources} connected sources and ${researchAssignments.length} active agent assignment(s).`,
      },
      ...prev,
    ].slice(0, 14));
  };

  const saveResearchCommit = () => {
    if (!researchHasRun || researchProgress < 1) return;
    const block = 76200000 + researchRunId * 41 + researchCommits.length * 3;
    const tx = pseudoTxHash(researchRunId * 97 + researchCommits.length * 13 + connectedSources * 7);
    const commit = {
      id: `commit-${researchRunId}-${researchCommits.length + 1}`,
      ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      tx,
      block,
      summary: `Evidence ${researcherMetrics.evidence.toFixed(1)} · Repro ${researcherMetrics.reproducibility.toFixed(1)} · Sources ${connectedSources}`,
    };
    setResearchCommits((prev) => [commit, ...prev].slice(0, 10));
    setResearchFindings((prev) => [
      {
        id: `finding-commit-${Date.now()}`,
        ts: commit.ts,
        type: 'CHAIN',
        text: `Result committed on Polygon (block ${block}, tx ${tx.slice(0, 12)}...).`,
      },
      ...prev,
    ].slice(0, 14));
  };

  return (
    <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep, padding: '12px' }}>
      {/* <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.14em', marginBottom: 10 }}>INTERACTIVE LAB PREVIEW (POST-LOGIN FEEL)</div> */}

      <div className="interactive-tab-strip">
        {LAB_TABS.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                padding: '10px 8px',
                border: 'none',
                borderRight: `1px solid ${C.b1}`,
                background: active ? `${tab.color}18` : 'transparent',
                color: active ? tab.color : C.t2,
                fontSize: 10,
                fontFamily: 'JetBrains Mono',
                letterSpacing: '.08em',
                cursor: 'pointer',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              {tab.label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {activeTab === 'learner' && (
        <div className="interactive-arch-grid">
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.surface, padding: '10px' }}>
              <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 8 }}>Layer Editor + Activation + Init</div>
              {learnerArchitecture.map((size, idx) => {
                const isInput = idx === 0;
                const isOutput = idx === learnerArchitecture.length - 1;
                const label = isInput ? 'INPUT' : isOutput ? 'OUTPUT' : `HIDDEN ${idx}`;
                return (
                  <div key={`layer-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 62, textAlign: 'right', color: C.t3, fontSize: 8 }}>{label}</div>
                    <input
                      type="number"
                      min={1}
                      max={96}
                      value={size}
                      disabled={isInput || isOutput}
                      onChange={(e) => updateLayerSize(idx, e.target.value)}
                      style={{
                        width: 52,
                        background: C.b0,
                        border: `1px solid ${C.b1}`,
                        color: isInput || isOutput ? C.t3 : C.t1,
                        padding: '4px 6px',
                        borderRadius: 3,
                        fontSize: 11,
                      }}
                    />
                    <div style={{ flex: 1, height: 4, background: C.b0, borderRadius: 2 }}>
                      <div
                        style={{
                          width: `${Math.max(4, (size / 96) * 100)}%`,
                          height: '100%',
                          borderRadius: 2,
                          background: isInput ? C.cyan : isOutput ? C.green : C.violet,
                        }}
                      />
                    </div>
                    <div style={{ width: 18, color: C.t3, fontSize: 8 }}>{size}</div>
                  </div>
                );
              })}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <button
                  onClick={addHiddenLayer}
                  style={{
                    padding: '7px 8px',
                    borderRadius: 3,
                    border: `1px solid ${C.green}55`,
                    background: `${C.green}16`,
                    color: C.green,
                    cursor: 'pointer',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '.08em',
                    fontFamily: 'JetBrains Mono',
                  }}
                >
                  + LAYER
                </button>
                <button
                  onClick={removeHiddenLayer}
                  style={{
                    padding: '7px 8px',
                    borderRadius: 3,
                    border: `1px solid ${C.red}55`,
                    background: `${C.red}16`,
                    color: C.red,
                    cursor: 'pointer',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '.08em',
                    fontFamily: 'JetBrains Mono',
                  }}
                >
                  - LAYER
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <MiniMetric label="DEPTH" value={`${learnerArchitecture.length} layers`} color={C.cyan} />
                <MiniMetric label="PARAMS" value={learnerTotalParams.toLocaleString()} color={C.green} />
              </div>

              <div style={{ borderTop: `1px solid ${C.b1}`, marginTop: 10, paddingTop: 10 }}>
                <div style={{ fontSize: 10, color: C.gold, fontWeight: 700, marginBottom: 8 }}>Activation + Init</div>
                {!learnerForwardData ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <select value={learnerActivation} onChange={(e) => setLearnerActivation(e.target.value)} style={{ width: '100%', background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                      <option value="gelu">GELU</option>
                      <option value="relu">ReLU</option>
                      <option value="swish">Swish</option>
                      <option value="tanh">Tanh</option>
                    </select>
                    <select value={learnerInit} onChange={(e) => setLearnerInit(e.target.value)} style={{ width: '100%', background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                      <option value="xavier">Xavier Init</option>
                      <option value="he">He Init</option>
                      <option value="lecun">LeCun Init</option>
                      <option value="random">Random Normal</option>
                      <option value="zeros">Zeros</option>
                    </select>
                    <div style={{ fontSize: 10, color: C.t2 }}>Learning Rate: {learnerLr.toFixed(3)}</div>
                    <button
                      onClick={runLearnerForwardPass}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 3,
                        border: `1px solid ${C.violet}66`,
                        background: `${C.violet}18`,
                        color: C.violet,
                        fontFamily: 'JetBrains Mono',
                        fontSize: 10,
                        letterSpacing: '.1em',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      FORWARD PASS
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0, padding: '8px 9px' }}>
                      <div style={{ fontSize: 8, color: C.t3, marginBottom: 4 }}>INPUT VECTOR</div>
                      <div style={{ fontSize: 10, color: C.t1, lineHeight: 1.5 }}>{learnerForwardData.input.map((v) => v.toFixed(3)).join(', ')}</div>
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {learnerForwardData.activations.map((layerVals, layerIdx) => (
                        <div key={`layer-act-${layerIdx}`} style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0, padding: '7px 8px' }}>
                          <div style={{ fontSize: 8, color: C.t3, marginBottom: 3 }}>
                            {layerIdx === 0 ? 'INPUT' : layerIdx === learnerForwardData.activations.length - 1 ? 'OUTPUT' : `HIDDEN ${layerIdx}`}
                          </div>
                          <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.5 }}>
                            {layerVals.slice(0, 10).map((v) => v.toFixed(3)).join(', ')}
                            {layerVals.length > 10 ? `, +${layerVals.length - 10} more` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ maxHeight: 220, overflow: 'auto', border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                        <thead>
                          <tr style={{ background: C.surface }}>
                            <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Layer</th>
                            <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Neuron</th>
                            <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>z (pre)</th>
                            <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>a (post)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {learnerForwardData.tableRows.map((row) => (
                            <tr key={row.key}>
                              <td style={{ padding: '5px 7px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.layer}</td>
                              <td style={{ padding: '5px 7px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.neuron}</td>
                              <td style={{ padding: '5px 7px', color: C.gold, borderBottom: `1px solid ${C.b1}` }}>{row.z.toFixed(4)}</td>
                              <td style={{ padding: '5px 7px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.a.toFixed(4)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      onClick={() => setLearnerForwardData(null)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 3,
                        border: `1px solid ${C.b2}`,
                        background: 'transparent',
                        color: C.t2,
                        fontFamily: 'JetBrains Mono',
                        fontSize: 9,
                        letterSpacing: '.08em',
                        cursor: 'pointer',
                      }}
                    >
                      BACK TO CONTROLS
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
              <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 8 }}>Network Topology (Live)</div>
              <TopologyDiagram layers={learnerArchitecture} activations={learnerForwardData?.activations} />
              <div style={{ display: 'flex', gap: 10, marginTop: 6, padding: '7px 8px', borderRadius: 3, background: C.surface, fontSize: 8, color: C.t3, flexWrap: 'wrap' }}>
                <span style={{ color: C.cyan }}>INPUT</span>
                <span style={{ color: C.violet }}>HIDDEN</span>
                <span style={{ color: C.green }}>OUTPUT</span>
                <span style={{ marginLeft: 'auto' }}>Nodes = neurons, edges = learned weights</span>
              </div>
            </div>

            {learnerForwardData && (
              <div className="interactive-learner-bottom">
                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                  <div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 8 }}>Forward Pass Dynamics</div>
                  <LinePreview points={learnerForwardStats?.line || []} color={C.green} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8, marginTop: 8 }}>
                    <MiniMetric label="MEAN |ACT|" value={(learnerForwardStats?.meanAbs || 0).toFixed(3)} color={C.cyan} />
                    <MiniMetric label="MAX |ACT|" value={(learnerForwardStats?.maxAbs || 0).toFixed(3)} color={C.green} />
                    <MiniMetric label="DEAD NODES" value={`${(learnerForwardStats?.deadRatio || 0).toFixed(1)}%`} color={C.violet} />
                  </div>
                </div>

                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                  <div style={{ fontSize: 10, color: C.violet, fontWeight: 700, marginBottom: 8 }}>Forward Layer Table</div>
                  <div style={{ maxHeight: 216, overflow: 'auto', border: `1px solid ${C.b1}`, borderRadius: 3, background: C.surface, marginBottom: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Layer</th>
                          <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Mean</th>
                          <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Min</th>
                          <th style={{ textAlign: 'left', padding: '6px 7px', color: C.t3, borderBottom: `1px solid ${C.b1}` }}>Max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(learnerForwardStats?.layerRows || []).map((row) => (
                          <tr key={row.layer}>
                            <td style={{ padding: '5px 7px', color: C.t2, borderBottom: `1px solid ${C.b1}` }}>{row.layer}</td>
                            <td style={{ padding: '5px 7px', color: C.cyan, borderBottom: `1px solid ${C.b1}` }}>{row.mean.toFixed(3)}</td>
                            <td style={{ padding: '5px 7px', color: C.gold, borderBottom: `1px solid ${C.b1}` }}>{row.min.toFixed(3)}</td>
                            <td style={{ padding: '5px 7px', color: C.green, borderBottom: `1px solid ${C.b1}` }}>{row.max.toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <MiniMetric label="OUTPUT CONF" value={(learnerForwardStats?.outputConfidence || 0).toFixed(3)} color={C.violet} />
                    <MiniMetric label="RUN ID" value={`#${learnerRunId}`} color={C.cyan} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'builder' && (
        <div className="interactive-preview-grid">
          <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.surface, padding: '10px' }}>
            <div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 8 }}>Your Dataset ID : 69420</div>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0, padding: '8px 9px', marginBottom: 9 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 9, color: C.t1, fontWeight: 700 }}>loan_approval_dataset_v4.csv</div>
                <div style={{ fontSize: 8, color: C.green }}>UPLOADED</div>
              </div>
              <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.5, marginBottom: 6 }}>
                rows: 48,732 | features: 26 | target: approved | missing values handled
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 7 }}>
                {['kaggle/import', 'binary-classification', 'class-balance 58:42'].map((tag) => (
                  <span key={tag} style={{ fontSize: 8, color: C.cyan, border: `1px solid ${C.cyan}33`, background: `${C.cyan}11`, borderRadius: 3, padding: '2px 6px' }}>
                    {tag}
                  </span>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 8 }}>
                <thead>
                  <tr>
                    {['credit_score', 'income', 'dti', 'approved'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', color: C.t3, borderBottom: `1px solid ${C.b1}`, padding: '4px 5px' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['742', '$84k', '0.28', '1'],
                    ['610', '$49k', '0.54', '0'],
                    ['695', '$67k', '0.33', '1'],
                  ].map((row, idx) => (
                    <tr key={`demo-row-${idx}`}>
                      {row.map((cell, cellIdx) => (
                        <td key={`${cell}-${cellIdx}`} style={{ color: C.t2, borderBottom: `1px solid ${C.b1}`, padding: '4px 5px' }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="builder-control-grid" style={{ marginBottom: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 9, color: C.t3, marginBottom: 5 }}>Optimizer</label>
                <select value={builderOptimizer} onChange={(e) => setBuilderOptimizer(e.target.value)} style={{ width: '100%', background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                  <option value="sgd">SGD</option>
                  <option value="adam">Adam</option>
                  <option value="adamw">AdamW</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 9, color: C.t3, marginBottom: 5 }}>Network Architecture</label>
                <select value={builderArchitecture} onChange={(e) => setBuilderArchitecture(e.target.value)} style={{ width: '100%', background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                  <option value="8">8 layers</option>
                  <option value="16">16 layers</option>
                  <option value="32">32 layers</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 9, color: C.t3, marginBottom: 5 }}>Train Split: {builderSplit}%</label>
                <input type="range" min="60" max="90" value={builderSplit} onChange={(e) => setBuilderSplit(Number(e.target.value))} style={{ width: '100%' }} />
              </div>
            </div>

            <div className="builder-action-grid">
              <button
                onClick={runBuilderTraining}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 3,
                  border: `1px solid ${C.green}66`,
                  background: `${C.green}18`,
                  color: C.green,
                  fontFamily: 'JetBrains Mono',
                  fontSize: 10,
                  letterSpacing: '.1em',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {builderIsTraining ? 'TRAINING...' : 'RUN AND TRAIN'}
              </button>
              <button
                onClick={saveBuilderVersion}
                disabled={builderEpochCursor === 0}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 3,
                  border: `1px solid ${C.violet}66`,
                  background: `${C.violet}18`,
                  color: builderEpochCursor === 0 ? C.t4 : C.violet,
                  fontFamily: 'JetBrains Mono',
                  fontSize: 10,
                  letterSpacing: '.1em',
                  fontWeight: 700,
                  cursor: builderEpochCursor === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                SAVE + VERSION
              </button>
            </div>

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '9px', marginTop: 8 }}>
              {!builderHasRun ? (
                <>
                  <div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 6 }}>Execution Stream</div>
                  <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.6, marginBottom: 8 }}>
                    Ready to run a training session on loan approval data. Click `RUN AND TRAIN` to start forward/backward passes.
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {[
                      'compile model graph',
                      'initialize weights',
                      'prepare dataloader batches',
                      'awaiting training command',
                    ].map((line) => (
                      <div key={line} style={{ fontSize: 8, color: C.t3 }}>
                        + {line}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>Execution Stream</div>
                    <div style={{ fontSize: 8, color: C.t3 }}>
                      epoch {Math.min(builderEpochCursor, builderEpochs)}/{builderEpochs}
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <LinePreview points={builderVisibleLoss} color={C.green} />
                  </div>
                  <div style={{ maxHeight: 112, overflow: 'auto', border: `1px solid ${C.b1}`, borderRadius: 3, background: C.surface, padding: '6px 7px' }}>
                    {builderTrace.map((row) => (
                      <div key={`trace-${row.epoch}`} style={{ fontSize: 8, color: C.t2, marginBottom: 4, lineHeight: 1.4 }}>
                        e{row.epoch.toString().padStart(2, '0')} | loss={row.loss.toFixed(3)} | grad={row.gradNorm.toFixed(3)} | lr={row.lr}
                      </div>
                    ))}
                  </div>

                 

                  
                </>

              )}
            </div>

 <br></br>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                    <div style={{ fontSize: 10, color: C.gold, fontWeight: 700 }}>Saved Versions</div>
                    <div style={{ fontSize: 8, color: C.t3 }}>local version history</div>
                  </div>
                  {builderVersions.length === 0 ? (
                    <div style={{ fontSize: 9, color: C.t3 }}>No saved versions yet. Run training and click SAVE + VERSION.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 6 }}>
                      {builderVersions.map((v) => (
                        <div key={`${v.id}-${v.stamp}`} style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.surface, padding: '7px 8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <div style={{ fontSize: 9, color: C.gold, fontWeight: 700 }}>{v.id}</div>
                            <div style={{ fontSize: 8, color: C.t3 }}>{v.stamp}</div>
                          </div>
                          <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.5 }}>
                            {v.optimizer} | {v.architecture} | split {v.split}% | loss {v.loss} | acc {v.acc}% | f1 {v.f1}%
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
               
               
                </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>Neural Network Topology</div>
                <div style={{ fontSize: 9, color: C.t3 }}>optimizer={builderOptimizer} | net={builderArchitecture}L</div>
              </div>
              <TopologyDiagram layers={builderNetworkLayers} activations={builderNetworkActivations} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginTop: 2 }}>
                <span style={{ color: C.t3 }}>
                  {builderHasRun ? 'Activations updating with training progress' : 'Architecture preview - run training to activate nodes'}
                </span>
                <span style={{ color: C.green }}>{builderHasRun ? `${(builderProgress * 100).toFixed(0)}% trained` : 'idle'}</span>
              </div>
            </div>

            {!builderHasRun ? (
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 7 }}>Awaiting Training Run</div>
                <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.7, marginBottom: 8 }}>
                  Loss curve, classification metrics, confusion matrix, and pipeline score will appear after `RUN AND TRAIN`.
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {[
                    `optimizer: ${builderOptimizer.toUpperCase()}`,
                    `network: ${builderArchitecture} layers`,
                    `train split: ${builderSplit}%`,
                    `dataset: loan approval`,
                  ].map((line) => (
                    <div key={line} style={{ fontSize: 8, color: C.t3 }}>
                      - {line}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                    <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>Training Curve</div>
                    <div style={{ fontSize: 9, color: C.t3 }}>run #{builderRunSeed}</div>
                  </div>
                  <LinePreview points={builderVisibleLoss} color={C.green} />
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 4 }}>
                      <span style={{ color: C.t2 }}>Training Progress</span>
                      <span style={{ color: C.green }}>{(builderProgress * 100).toFixed(0)}%</span>
                    </div>
                    <ProgressBar value={builderProgress * 100} color={C.green} />
                  </div>
                  <div className="builder-metrics-grid" style={{ marginTop: 8 }}>
                    <MiniMetric label="LOSS" value={builderCurrentLoss !== null ? builderCurrentLoss.toFixed(3) : '--'} color={C.green} />
                    <MiniMetric label="ACCURACY" value={`${builderMetrics.accuracy.toFixed(1)}%`} color={C.cyan} />
                    <MiniMetric label="F1 SCORE" value={`${builderMetrics.f1.toFixed(1)}%`} color={C.violet} />
                    <MiniMetric label="AUC" value={`${builderMetrics.auc.toFixed(1)}%`} color={C.gold} />
                    <MiniMetric label="LATENCY" value={`${builderMetrics.latency.toFixed(0)} ms`} color={C.orange} />
                  </div>
                  
                </div>

                <div className="builder-bottom-grid">
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                    <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 7 }}>Classification Metrics</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                      <tbody>
                        {[
                          ['Precision', `${builderMetrics.precision.toFixed(2)}%`],
                          ['Recall', `${builderMetrics.recall.toFixed(2)}%`],
                          ['F1', `${builderMetrics.f1.toFixed(2)}%`],
                          ['ROC-AUC', `${builderMetrics.auc.toFixed(2)}%`],
                        ].map(([label, val]) => (
                          <tr key={label}>
                            <td style={{ color: C.t3, borderBottom: `1px solid ${C.b1}`, padding: '5px 6px' }}>{label}</td>
                            <td style={{ color: C.t1, borderBottom: `1px solid ${C.b1}`, padding: '5px 6px', textAlign: 'right' }}>{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 8, fontSize: 8, color: C.t3 }}>Validation window: 2,900 records</div>
                  </div>

                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                    <div style={{ fontSize: 10, color: C.violet, fontWeight: 700, marginBottom: 7 }}>Confusion Matrix</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <MiniMetric label="TP" value={builderConfusion.tp} color={C.green} />
                      <MiniMetric label="FP" value={builderConfusion.fp} color={C.red} />
                      <MiniMetric label="FN" value={builderConfusion.fn} color={C.orange} />
                      <MiniMetric label="TN" value={builderConfusion.tn} color={C.cyan} />
                    </div>
                  </div>
                </div>

                
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'researcher' && (
        <div className="researcher-layout">
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.surface, padding: '10px' }}>
              <div style={{ fontSize: 10, color: C.violet, fontWeight: 700, marginBottom: 8 }}>Research Orchestration Controls</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div className="research-select-grid">
                  <select value={researchInit} onChange={(e) => setResearchInit(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    <option value="xavier">Xavier Init</option>
                    <option value="he">He Init</option>
                    <option value="orthogonal">Orthogonal Init</option>
                  </select>
                  <select value={researchAlgo} onChange={(e) => setResearchAlgo(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    <option value="adamw">AdamW</option>
                    <option value="lion">Lion</option>
                    <option value="shampoo">Shampoo</option>
                  </select>
                  <select value={researchActivation} onChange={(e) => setResearchActivation(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    <option value="gelu">GELU</option>
                    <option value="swish">Swish</option>
                    <option value="relu">ReLU</option>
                  </select>
                </div>

                <label style={{ display: 'block', fontSize: 9, color: C.t3 }}>
                  Depth: {researchDepth}
                  <input type="range" min="6" max="32" value={researchDepth} onChange={(e) => setResearchDepth(Number(e.target.value))} style={{ width: '100%' }} />
                </label>

                <label style={{ display: 'block', fontSize: 9, color: C.t3 }}>
                  Priority: P{researchPriority}
                  <input type="range" min="1" max="5" value={researchPriority} onChange={(e) => setResearchPriority(Number(e.target.value))} style={{ width: '100%' }} />
                </label>

                <div className="research-select-grid">
                  <select value={researchSchedule} onChange={(e) => setResearchSchedule(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    <option value="stream">Streaming (continuous)</option>
                    <option value="hourly">Hourly schedule</option>
                    <option value="daily">Daily schedule</option>
                    <option value="manual">Manual schedule</option>
                  </select>
                  <select value={researchAgent} onChange={(e) => setResearchAgent(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    {AGENTS.map((agent) => (
                      <option key={agent.name} value={agent.name}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                  <select value={researchTask} onChange={(e) => setResearchTask(e.target.value)} style={{ background: C.b0, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 3, padding: '6px' }}>
                    <option value="Draft hypothesis report">Draft hypothesis report</option>
                    <option value="Run ablation matrix">Run ablation matrix</option>
                    <option value="Cross-check data leakage">Cross-check data leakage</option>
                    <option value="Prepare literature synthesis">Prepare literature synthesis</option>
                  </select>
                </div>

                <div className="research-action-grid">
                  <button
                    onClick={assignResearchWork}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 3,
                      border: `1px solid ${C.cyan}66`,
                      background: `${C.cyan}16`,
                      color: C.cyan,
                      fontFamily: 'JetBrains Mono',
                      fontSize: 9,
                      letterSpacing: '.08em',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    ASSIGN AGENT WORK
                  </button>
                  <button
                    onClick={scheduleResearchExperiment}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 3,
                      border: `1px solid ${C.gold}66`,
                      background: `${C.gold}14`,
                      color: C.gold,
                      fontFamily: 'JetBrains Mono',
                      fontSize: 9,
                      letterSpacing: '.08em',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    SCHEDULE EXPERIMENT
                  </button>
                  <button
                    onClick={runResearchScenario}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 3,
                      border: `1px solid ${C.violet}66`,
                      background: `${C.violet}18`,
                      color: C.violet,
                      fontFamily: 'JetBrains Mono',
                      fontSize: 9,
                      letterSpacing: '.08em',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {researchIsRunning ? 'RUNNING RESEARCH...' : 'RUN RESEARCH CYCLE'}
                  </button>
                  <button
                    onClick={saveResearchCommit}
                    disabled={!researchHasRun || researchProgress < 1}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 3,
                      border: `1px solid ${C.green}66`,
                      background: `${C.green}16`,
                      color: !researchHasRun || researchProgress < 1 ? C.t4 : C.green,
                      fontFamily: 'JetBrains Mono',
                      fontSize: 9,
                      letterSpacing: '.08em',
                      fontWeight: 700,
                      cursor: !researchHasRun || researchProgress < 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    SAVE + COMMIT POLYGON
                  </button>
                </div>
              </div>
            </div>

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
              <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 8 }}>Connected Data Sources</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {researchSources.map((source) => (
                  <div key={source.id} style={{ border: `1px solid ${source.connected ? `${C.cyan}44` : C.b1}`, borderRadius: 3, background: C.surface, padding: '7px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={source.connected} onChange={() => toggleResearchSource(source.id)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 9, color: C.t1, fontWeight: 700 }}>{source.name}</div>
                        <div style={{ fontSize: 8, color: C.t3 }}>
                          {source.type} | {source.rows}
                        </div>
                      </div>
                      <div style={{ fontSize: 8, color: source.connected ? C.green : C.t4 }}>{source.connected ? 'LIVE' : 'OFF'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="research-mini-grid">
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                <div style={{ fontSize: 10, color: C.gold, fontWeight: 700, marginBottom: 7 }}>Schedule Queue</div>
                {researchScheduleQueue.length === 0 ? (
                  <div style={{ fontSize: 9, color: C.t3 }}>No queued experiments yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 5 }}>
                    {researchScheduleQueue.map((item) => (
                      <div key={item.id} style={{ fontSize: 8, color: C.t2 }}>
                        {item.ts} | {item.cadence} | {item.config}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                <div style={{ fontSize: 10, color: C.violet, fontWeight: 700, marginBottom: 7 }}>Agent Assignments</div>
                {researchAssignments.length === 0 ? (
                  <div style={{ fontSize: 9, color: C.t3 }}>Assign work to AI agents.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 5 }}>
                    {researchAssignments.map((assignment) => (
                      <div key={assignment.id} style={{ fontSize: 8, color: C.t2 }}>
                        {assignment.agent} | {assignment.task} | P{assignment.priority}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: C.violet, fontWeight: 700 }}>Research Graph + Objective</div>
                <div style={{ fontSize: 9, color: C.t3 }}>
                  run #{researchRunId} | {researchIsRunning ? 'active' : researchHasRun ? 'completed' : 'idle'}
                </div>
              </div>
              <TopologyDiagram layers={researchTopologyLayers} activations={researchTopologyActivations} />
              <div style={{ marginTop: 8 }}>
                {researchHasRun ? (
                  <LinePreview points={researchObjectiveLine} color={C.violet} />
                ) : (
                  <div style={{ fontSize: 9, color: C.t3, padding: '8px 0' }}>Run a research cycle to generate objective convergence.</div>
                )}
              </div>
            </div>

            <div className="research-metrics-grid">
              <MiniMetric label="STABILITY" value={researchHasRun ? `${researcherMetrics.stability.toFixed(1)}%` : '--'} color={C.violet} />
              <MiniMetric label="NOVELTY" value={researchHasRun ? `${researcherMetrics.novelty.toFixed(1)}%` : '--'} color={C.cyan} />
              <MiniMetric label="REPRODUCIBILITY" value={researchHasRun ? `${researcherMetrics.reproducibility.toFixed(1)}%` : '--'} color={C.green} />
              <MiniMetric label="EVIDENCE SCORE" value={researchHasRun ? `${researcherMetrics.evidence.toFixed(1)}%` : '--'} color={C.gold} />
              <MiniMetric label="AGENT SYNC" value={researchHasRun ? `${researcherMetrics.agentSync.toFixed(1)}%` : '--'} color={C.orange} />
            </div>

            <div className="research-stream-grid">
              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                <div style={{ fontSize: 10, color: C.cyan, fontWeight: 700, marginBottom: 7 }}>Research Findings Stream</div>
                {researchFindings.length === 0 ? (
                  <div style={{ fontSize: 9, color: C.t3 }}>No research events yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {researchFindings.map((finding) => (
                      <div key={finding.id} style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.surface, padding: '6px 7px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontSize: 8, color: C.cyan }}>{finding.type}</span>
                          <span style={{ fontSize: 8, color: C.t3 }}>{finding.ts}</span>
                        </div>
                      <div style={{ fontSize: 9, color: C.t2, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{finding.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
                <div style={{ fontSize: 10, color: C.violet, fontWeight: 700, marginBottom: 7 }}>AI Agent Collaboration Board</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {researchAgentBoard.map((agent) => (
                    <div key={agent.name} style={{ border: `1px solid ${agent.color}33`, borderRadius: 3, background: C.surface, padding: '6px 7px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: agent.color, fontWeight: 700 }}>{agent.name}</span>
                        <span style={{ fontSize: 8, color: C.t3 }}>{agent.spec}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 8, color: agent.status === 'ACTIVE' ? C.green : agent.status === 'READY' ? C.cyan : C.t4 }}>
                          {agent.status}
                        </span>
                      </div>
                      <div style={{ fontSize: 8, color: C.t2, marginBottom: 4 }}>{agent.assignedTask}</div>
                      <ProgressBar value={agent.utilization} color={agent.color} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ border: `1px solid ${C.b1}`, borderRadius: 4, background: C.b0, padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>Polygon Commit Journal</div>
                <div style={{ fontSize: 8, color: C.t3 }}>chain: polygon-pos</div>
              </div>
              {researchCommits.length === 0 ? (
                <div style={{ fontSize: 9, color: C.t3 }}>No on-chain commit yet. Complete a run and click `SAVE + COMMIT POLYGON`.</div>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {researchCommits.map((commit) => (
                    <div key={commit.id} style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.surface, padding: '7px 8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <div style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>block #{commit.block}</div>
                        <div style={{ fontSize: 8, color: C.t3 }}>{commit.ts}</div>
                      </div>
                      <div style={{ fontSize: 8, color: C.cyan, marginBottom: 2, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{commit.tx}</div>
                      <div style={{ fontSize: 9, color: C.t2 }}>{commit.summary}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,5,8,.58)',
        backdropFilter: 'blur(7px)',
        WebkitBackdropFilter: 'blur(7px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 80,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '80vw',
          height: '80vh',
          maxWidth: '1280px',
          maxHeight: '820px',
          minHeight: '320px',
          background: '#000',
          border: `1px solid ${C.b1}`,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,.55)',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 2,
            background: 'rgba(6,11,20,.8)',
            border: `1px solid ${C.b1}`,
            color: C.t2,
            borderRadius: 4,
            width: 32,
            height: 32,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
          }}
          aria-label="Close demo"
        >
          x
        </button>
        <video
          controls
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
        >
          <source src={DEMO_VIDEO_SRC} type="video/mp4" />
          Your browser cannot play this video.
        </video>
      </div>
    </div>
  );
}

function PublicLanding({ onSignUp, onLogin }) {
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  useEffect(() => {
    if (!isDemoOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsDemoOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDemoOpen]);

  return (
    <div style={{ position: 'relative', background: C.void, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
      <style>{`
        .home-h1 { font-family:'Syne',sans-serif; font-weight:800; letter-spacing:-.02em; line-height:1.08; }
        .home-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .home-grid-labs { display:grid; grid-template-columns:repeat(auto-fill,minmax(132px,1fr)); gap:7px; }
        .home-designed-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; }
        .home-features-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; }
        .home-problem-grid { display:grid; grid-template-columns:1.1fr .9fr; gap:8px; }
        .home-diff-list { display:grid; gap:8px; }
        .home-diff-item { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .home-proof-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; }
        .home-pricing-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; }
        .jorvex-stack-row { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
        .interactive-tab-strip { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); border:1px solid ${C.b1}; border-radius:4px; overflow:hidden; margin-bottom:12px; background:${C.surface}; }
        .interactive-tab-strip > button:last-child { border-right:none !important; }
        .interactive-arch-grid { display:grid; grid-template-columns:340px 1fr; gap:8px; }
        .interactive-learner-bottom { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .interactive-preview-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .builder-control-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .builder-action-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .builder-metrics-grid { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:8px; }
        .builder-bottom-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .researcher-layout { display:grid; grid-template-columns:minmax(300px,340px) minmax(0,1fr); gap:8px; align-items:start; }
        .researcher-layout > * { min-width:0; }
        .research-action-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .research-mini-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .research-mini-grid > * { min-width:0; }
        .research-metrics-grid { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:8px; }
        .research-stream-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .research-stream-grid > * { min-width:0; }
        .research-select-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; }
        .research-select-grid > *, .research-action-grid > * { min-width:0; }
        @media (max-width:1180px) { .home-designed-grid, .home-features-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:1080px) { .home-grid-2, .home-designed-grid, .home-features-grid, .home-problem-grid, .home-diff-item, .interactive-preview-grid, .interactive-arch-grid, .interactive-learner-bottom, .researcher-layout, .research-mini-grid, .research-stream-grid { grid-template-columns:1fr; } .home-proof-grid, .home-pricing-grid, .jorvex-stack-row { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:860px)  { .builder-metrics-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:900px)  { .research-metrics-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:760px)  { .research-select-grid, .research-action-grid, .builder-control-grid, .builder-action-grid, .builder-bottom-grid, .home-proof-grid, .home-pricing-grid, .jorvex-stack-row { grid-template-columns:1fr; } }
      `}</style>

      <div style={{ padding: '34px 24px 28px', borderBottom: `1px solid ${C.b1}`, position: 'relative', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            top: '-28%',
            right: '-6%',
            width: 420,
            height: 420,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${C.cyan}14, transparent 70%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-24%',
            left: '4%',
            width: 360,
            height: 360,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${C.violet}12, transparent 70%)`,
          }}
        />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 11px', background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 2, marginBottom: 16 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} />
            <span style={{ fontSize: 8, color: C.cyan, letterSpacing: '.14em' }}>AI RESEARCH ENGINE</span>
          </div>

          <h1 className="home-h1" style={{ fontSize: 'clamp(26px,4vw,42px)', color: C.t1, margin: '0 0 10px' }}>
            Build and research AI
            <br />
            <span style={{ color: C.cyan }}>in one workspace.</span>
          </h1>

          <p style={{ maxWidth: 680, margin: '0 0 18px', color: C.t2, fontSize: 12, lineHeight: 1.8 }}>
Analyze datasets, run AI/ML experiments, and automate your AI research with our in-built AI agents. All in your browser, 100% local and private.          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <button
              onClick={() => setIsDemoOpen(true)}
              style={{
                padding: '10px 16px',
                borderRadius: 3,
                border: `1px solid ${C.cyan}66`,
                background: C.cyan,
                color: C.void,
                fontFamily: 'JetBrains Mono',
                fontSize: 10,
                letterSpacing: '.1em',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              WATCH DEMO
            </button>
            <button
              onClick={onSignUp}
              style={{
                padding: '10px 14px',
                borderRadius: 3,
                border: `1px solid ${C.b2}`,
                background: 'transparent',
                color: C.t2,
                fontFamily: 'JetBrains Mono',
                fontSize: 10,
                letterSpacing: '.1em',
                cursor: 'pointer',
              }}
            >
              SIGN UP
            </button>
          </div>
        </div>
      </div>

      <div>

        <Reveal>
          <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.14em', marginBottom: 10 }}>KEY FEATURES</div>
            <div className="home-features-grid">
              {KEY_FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  style={{
                    border: `1px solid ${feature.color}55`,
                    borderRadius: 4,
                    background: `linear-gradient(145deg, ${feature.color}11, ${C.surface})`,
                    padding: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: feature.color, letterSpacing: '.08em' }}>{feature.icon}</div>
                    <div style={{ width: 28, height: 2, background: `${feature.color}88` }} />
                  </div>
                  <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, marginBottom: 6, lineHeight: 1.4 }}>{feature.title}</div>
                  <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.65 }}>{feature.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.04}>
          <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 8, color: C.red, letterSpacing: '.14em', marginBottom: 9 }}>1. THE PROBLEM</div>
            <div className="home-problem-grid">
              <div style={{ border: `1px solid ${C.red}44`, borderRadius: 4, background: `linear-gradient(155deg, ${C.red}10, ${C.surface})`, padding: '12px' }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, color: C.t1, marginBottom: 8, lineHeight: 1.2 }}>
                  Your Work Is Fragmented.
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {[
                    'You learn in one place.',
                    'Run experiments in another.',
                    'Track results manually.',
                    'Use AI tools separately.',
                    'Research in different places.',
                    'No dots are connected.',
                    'Nothing remembers your work.',
                  ].map((line, i) => (
                    <div key={line} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 7px', border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0 }}>
                      <span style={{ minWidth: 16, height: 16, borderRadius: '50%', border: `1px solid ${C.red}55`, color: C.red, fontSize: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                        {i + 1}
                      </span>
                      <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.55 }}>{line}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 8, color: C.cyan, letterSpacing: '.14em' }}>2. THE SHIFT</div>
                <div style={{ border: `1px solid ${C.cyan}44`, borderRadius: 4, background: `linear-gradient(150deg, ${C.cyan}10, ${C.surface})`, padding: '12px' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, color: C.t1, marginBottom: 8, lineHeight: 1.2 }}>
                    JORVEX unifies it.
                  </div>
                  <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.7, marginBottom: 12 }}>
                    One environment for learning, experiments, AI assistance, research memory, and verifiable outputs.
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {[
                      { label: 'LEARN + BUILD + RESEARCH', val: 'IN ONE FLOW', color: C.cyan },
                      { label: 'EXPERIMENT MEMORY', val: 'ALWAYS ON', color: C.green },
                      { label: 'RESEARCH CONTEXT', val: 'NOT LOST', color: C.violet },
                      { label: 'RESULT INTEGRITY', val: 'VERIFIABLE', color: C.gold },
                    ].map((item) => (
                      <div key={item.label} style={{ padding: '7px 8px', borderRadius: 3, border: `1px solid ${item.color}44`, background: `${item.color}10` }}>
                        <div style={{ fontSize: 7, color: C.t3, letterSpacing: '.1em' }}>{item.label}</div>
                        <div style={{ fontSize: 10, color: item.color, fontWeight: 700 }}>{item.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 8, color: C.violet, letterSpacing: '.14em', marginBottom: 9 }}>3. WHAT MAKES THIS DIFFERENT</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 28, color: C.t1, lineHeight: 1.15, marginBottom: 8 }}>
              Not Just Another AI Tool.
            </div>
            <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, marginBottom: 12, maxWidth: 840 }}>
              Builders today juggle notebooks, trackers, cloud setup, and assistants that do not share context.
              Jorvex unifies learning, experimentation, and AI collaboration in one workspace.
            </div>

            <div className="home-diff-list">
              {DIFFERENTIATION.map((item) => (
                <div key={item.tool} className="home-diff-item" style={{ border: `1px solid ${item.color}44`, borderRadius: 4, background: C.surface, padding: 8 }}>
                  <div style={{ border: `1px solid ${C.b1}`, borderRadius: 3, background: C.b0, padding: '9px 10px' }}>
                    <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.12em', marginBottom: 6 }}>COMMON TOOLING</div>
                    <div style={{ fontSize: 10, color: item.color, fontWeight: 700, marginBottom: 6 }}>{item.tool}</div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {item.points.map((p) => (
                        <div key={p} style={{ fontSize: 9, color: C.t2, lineHeight: 1.5 }}>
                          - {p}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${item.color}44`, borderRadius: 3, background: `linear-gradient(145deg, ${item.color}10, ${C.b0})`, padding: '9px 10px' }}>
                    <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.12em', marginBottom: 6 }}>JORVEX</div>
                    <div style={{ fontSize: 10, color: C.t1, lineHeight: 1.65 }}>
                      {item.degen}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12, border: `1px solid ${C.cyan}44`, borderRadius: 4, background: `linear-gradient(145deg, ${C.cyan}12, ${C.surface})`, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, lineHeight: 1.5 }}>
                Most tools help you run code.
                <span style={{ color: C.cyan }}> Jorvex helps you build research memory.</span>
              </div>
            </div>

            <div style={{ marginTop: 14, border: `1px solid ${C.b1}`, borderRadius: 4, background: `linear-gradient(180deg, ${C.surface}, ${C.deep})`, padding: '12px' }}>
              <div style={{ fontSize: 8, color: C.gold, letterSpacing: '.14em', marginBottom: 7 }}>THE MODERN RESEARCH STACK</div>
               
              <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, marginBottom: 12, maxWidth: 840 }}>

                Kaggle for datasets. Hugging Face for models. Jorvex for data analysis and experiments.
              </div>
              
              <JorvexStackRow
                mono="JetBrains Mono, monospace"
                surface={C.surface}
                voidBg={C.void}
                border={C.b1}
                text={C.t1}
                muted={C.t2}
                showActions
                primaryLabel="SIGN UP"
                onPrimaryAction={onSignUp}
                secondaryLabel="LOGIN"
                onSecondaryAction={onLogin}
              />
            </div>
          </div>
        </Reveal>


        <Reveal>
          <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 8, color: C.t3, letterSpacing: '.14em', marginBottom: 10 }}>WHO THIS PLATFORM IS DESIGNED FOR</div>
            <div className="home-designed-grid">
              {DESIGNED_FOR.map((item) => (
                <div
                  key={item.title}
                  style={{
                    border: `1px solid ${item.color}55`,
                    borderRadius: 4,
                    background: `linear-gradient(145deg, ${item.color}10, ${C.surface})`,
                    padding: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 9, color: item.color, letterSpacing: '.08em', fontWeight: 700 }}>{item.icon}</div>
                    <div style={{ width: 28, height: 2, background: `${item.color}88` }} />
                  </div>
                  <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, marginBottom: 6, lineHeight: 1.4 }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.65 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.02}>
          <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.b1}` }}>
            <InteractiveLabsSection />
          </div>
        </Reveal>

        

       

        
      </div>

      <div style={{ borderTop: `1px solid ${C.b1}`, padding: '26px 20px', textAlign: 'center', background: `linear-gradient(180deg, transparent, ${C.deep})` }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 26, color: C.t1, marginBottom: 8 }}>
          Start Building AI.
          <br />
          <span style={{ color: C.cyan }}>Start Researching AI.</span>
        </div>
        <div style={{ color: C.t2, fontSize: 11, marginBottom: 14 }}>Build your experiments. Build your research history. Build your edge.</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setIsDemoOpen(true)}
            style={{
              padding: '10px 18px',
              borderRadius: 3,
              border: 'none',
              background: C.cyan,
              color: C.void,
              fontFamily: 'JetBrains Mono',
              fontSize: 10,
              letterSpacing: '.12em',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            WATCH DEMO
          </button>
          <button
            onClick={onSignUp}
            style={{
              padding: '10px 14px',
              borderRadius: 3,
              border: `1px solid ${C.b2}`,
              background: 'transparent',
              color: C.t2,
              fontFamily: 'JetBrains Mono',
              fontSize: 10,
              letterSpacing: '.12em',
              cursor: 'pointer',
            }}
          >
            SIGN UP
          </button>
        </div>
      </div>

      <DemoModal open={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </div>
  );
}

function InternalWorkspace({ authDisplayName }) {
  return <ResearchWorkspace authDisplayName={authDisplayName} />;
}

function HomeHubLoadingScreen() {
  return (
    <div
      style={{
        minHeight: '100%',
        display: 'grid',
        placeItems: 'center',
        padding: '32px 20px',
        background: `
          radial-gradient(circle at top, rgba(34, 211, 238, 0.14), transparent 32%),
          linear-gradient(180deg, ${C.void}, ${C.deep})
        `,
      }}
    >
      <div
        style={{
          width: 'min(420px, 100%)',
          border: `1px solid ${C.b1}`,
          borderRadius: 6,
          background: `linear-gradient(145deg, ${C.surface}, ${C.deep})`,
          boxShadow: `0 24px 80px ${C.void}cc`,
          padding: '28px 22px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            margin: '0 auto 16px',
            borderRadius: '50%',
            border: `2px solid ${C.b1}`,
            borderTopColor: C.cyan,
            borderRightColor: C.violet,
            animation: 'homeHubSpin 0.9s linear infinite',
          }}
        />
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: '.18em', marginBottom: 8 }}>
          RESTORING SESSION
        </div>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 26, color: C.t1, marginBottom: 8 }}>
          Loading your workspace
        </div>
        <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7 }}>
          Checking your token and preparing the research workspace before the center view renders.
        </div>
        <div
          style={{
            marginTop: 18,
            height: 4,
            borderRadius: 999,
            background: C.b0,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: '36%',
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${C.cyan}, ${C.violet})`,
              animation: 'homeHubLoadingBar 1.2s ease-in-out infinite',
              transformOrigin: 'left center',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function HomeHub({
  isAuthenticated,
  authLoading = false,
  authDisplayName,
  authUser,
  isPremium,
  usageSummary,
  onNavigate,
  onAuthOpen,
  onUpgrade,
}) {
  const handleSignUp = () => onAuthOpen?.('signup');
  const handleLogin = () => onAuthOpen?.('login');

  return (
    <div style={{ height: '100%', minHeight: 0, overflowY: authLoading ? 'hidden' : 'auto', overflowX: 'hidden' }}>
      <style>{`
        @keyframes homeHubSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes homeHubLoadingBar {
          0% { transform: translateX(-130%) scaleX(0.7); }
          50% { transform: translateX(90%) scaleX(1); }
          100% { transform: translateX(260%) scaleX(0.72); }
        }
      `}</style>
      {authLoading ? (
        <HomeHubLoadingScreen />
      ) : !isAuthenticated ? (
        <PublicLanding onSignUp={handleSignUp} onLogin={handleLogin} />
      ) : (
        <HomeHubShell
          authDisplayName={authDisplayName}
          authUser={authUser}
          isAuthenticated={isAuthenticated}
          isPremium={isPremium}
          usageSummary={usageSummary}
          onNavigate={onNavigate}
          onAuthOpen={onAuthOpen}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
}
